import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { saveAs } from 'file-saver';
import isMobile from 'is-mobile';
import {
  useCreateLetterStore,
  useLetterContentStore,
  useDownloadLetterStore,
  useArchiveLetterStore,
  useViewLetterStore,
  useCreateLetterWithAttachmentsStore,
} from '@lib/store';
import { useScrollLock } from '@shared/hooks';
import { cn, PrintUtils } from '@shared/utils';
import type { AttachmentsType, GetLettersResponse, CreateLetterWithAttachment, CreateLettersRequest } from '@shared/api';
// import { SentSuccessfullyModal } from './sentSuccessfullyModal';
import { Attachments, UploadFile, Button } from '@shared/widgets';
import CloseIcon from '@assets/images/contact/close.svg';
import DeleteIcon from '@assets/images/contact/delete.svg';
import DownloadIcon from '@assets/images/download.svg';
import DownloadMobileIcon from '@assets/images/letters/download_ico.svg';
import PrintIcon from '@assets/images/print.svg';
import PrintMobileIcon from '@assets/images/letters/print_ico.svg';
import ArchiveMobileIcon from '@assets/images/archive-colored-icon_mobile.svg';
import TrashMobileIcon from '@assets/images/trash-icon_mobile.svg';

interface IForm {
  email: string;
  subject: string;
  body: string;
  files?: File[];

  pdf: Blob | null;
}
const DEFAULT_VALUES: IForm = {
  email: 'cs.unit@insignia.com',
  subject: '',
  body: '',
  files: undefined,

  pdf: null,
};

interface LetterPopUpProps {
  className?: string;
  isOpen?: boolean;
  onClose: () => void;
  data?: GetLettersResponse | null;
  onMinimizeLetter?: (letter: GetLettersResponse) => void;
  onDelete?: (item: GetLettersResponse) => void;
  onSubmit?: () => void;
  onReply?: () => void;
}

export const LetterPopUp = (props: LetterPopUpProps) => {
  const letterContentStore = useLetterContentStore();
  const createLetterStore = useCreateLetterStore();
  const createLetterWithAttachmentsStore = useCreateLetterWithAttachmentsStore();
  const archiveLettersStore = useArchiveLetterStore();
  const viewLetterStore = useViewLetterStore();
  const downloadLetterStore = useDownloadLetterStore();

  const lettersView = PrintUtils.openPDF(viewLetterStore.data!);

  const [groupId, setGroupId] = useState<string | undefined>();
  // const [showSentSuccessfullyModal, setShowSentSuccessfullyModal] = useState<boolean>(false);
  const emailLabelId = useId();

  const { control, formState, handleSubmit, setValue, watch, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const pdf = watch('pdf');

  const { lock, unlock } = useScrollLock();
  useEffect(() => {
    props.isOpen ? lock() : unlock();
    return () => unlock();
  }, [props.isOpen]);

  useEffect(() => {
    if (props.data) setValue('subject', !props.data?.outgoing ? `RE: ${props.data.subject}` : props.data.subject);
  }, [props.data]);
  useEffect(() => {
    if (letterContentStore.data) setValue('body', letterContentStore.data.body);
  }, [letterContentStore.data]);

  useEffect(() => {
    if (viewLetterStore.data) setValue('pdf', viewLetterStore.data);
  }, [viewLetterStore.data]);

  // useEffect(() => {
  //   if (createLetterStore.success) {
  //     closeHandler();
  //     setTimeout(() => {
  //       if (createLetterStore.success === false) sendModalSuccess();
  //       createLetterStore.clear();
  //     }, 300);
  //   }
  // }, [createLetterStore.success]);

  // const sendModalSuccess = () => {
  //   setShowSentSuccessfullyModal(true);
  //   // props.onOpenLetter && props.onOpenLetter(null);
  // };

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
    createLetterStore.clear();
    letterContentStore.clear();
    downloadLetterStore.clear();
    viewLetterStore.clear();
  };
  const minimizeHandler = () => {
    if (props.data) props.onMinimizeLetter?.(props.data);
    props.onClose();
    setValue('subject', '');
    setValue('body', '');
    setValue('pdf', null);
  };
  const submitHandler = async (data: IForm) => {
    if (letterContentStore.data) closeHandler();
    if (data.files) {
      const formData = new FormData();
      formData.append('subject', data.subject);
      formData.append('body', data.body);
      if (groupId) formData.append('groupId', groupId);
      data.files.forEach((file) => formData.append('file', file));
      await createLetterWithAttachmentsStore.createLetterWithAttachments(formData as unknown as CreateLetterWithAttachment);
      props.onSubmit?.();
      closeHandler();
    } else {
      const request: CreateLettersRequest = { subject: data.subject, body: data.body };
      if (groupId) request.groupId = groupId;
      await createLetterStore.createLetter(request);
      props.onSubmit?.();
      closeHandler();
    }
  };

  const archiveLetterHandler = async (id: string) => {
    if (archiveLettersStore.loading) return;
    await archiveLettersStore.archiveLetter(id!);
    closeHandler();
  };
  const deleteHandler = () => {
    props.onDelete?.(props.data!);
    closeHandler();
  };
  const downloadAttachmentHandler = (file: AttachmentsType) => {
    if (downloadLetterStore.loadingIds.has(file.id)) return;
    downloadLetterStore.get(file.id, file.fileName);
  };
  const replyHandler = () => {
    setGroupId(props.data?.id);
    props.onReply?.();
    setValue('body', '');
    setValue('pdf', null);
  };

  if (!props.isOpen) return null;
  return (
    <>
      <div className={cn('fixed top-0 right-0 bottom-0 left-0 z-10 bg-[#00000099]', props.className)}>
        <div
          className={cn(
            'absolute right-10 bottom-0 m-auto flex w-150 flex-col rounded-2xl',
            'max-md:inset-4 max-md:flex max-md:w-auto max-md:overflow-y-auto',
          )}
        >
          <div className='flex justify-between rounded-t-2xl bg-[#272727] p-4'>
            <div className='text-[#e5c496] uppercase'>{props.data ? props.data.subject : 'New message'}</div>
            <div className='flex items-center'>
              <div className='ml-2 cursor-pointer' onClick={minimizeHandler}>
                <div className='my-1 h-0.5 w-3 bg-[#e5c496]' />
              </div>
              <div className='ml-2 cursor-pointer' onClick={closeHandler}>
                <CloseIcon />
              </div>
            </div>
          </div>
          <div className={cn('rounded-b-2xl bg-white px-4 pb-4')}>
            <form onSubmit={handleSubmit(submitHandler)}>
              <Controller
                name='email'
                control={control}
                rules={{ required: 'This field is required' }}
                render={({ field }) => (
                  <div className='flex items-center border-b border-b-[#979797] py-2'>
                    <label htmlFor={emailLabelId} className='mr-4 inline-block w-15'>
                      {!props.data || props.data?.outgoing ? 'To' : 'Form'}
                    </label>
                    <input {...field} id={emailLabelId} className='grow border-none focus:outline-none' type='email' readOnly />
                  </div>
                )}
              />
              <Controller
                name='subject'
                control={control}
                rules={{ required: 'This field is required' }}
                render={({ field }) => (
                  <div className='flex items-center border-b border-b-[#979797] py-2'>
                    <label htmlFor='subject' className='mr-4 inline-block w-15'>
                      Subject
                    </label>
                    <input
                      {...field}
                      id='subject'
                      className='grow border-none focus:outline-none'
                      type='text'
                      required
                      readOnly={props.data?.outgoing}
                    />
                  </div>
                )}
              />
              {pdf && (
                <>
                  {isMobile() && (
                    <Button className='mt-5' fullWidth onClick={() => window.open(lettersView, '_blank', 'noopener,noreferrer')}>
                      View
                    </Button>
                  )}
                  {!isMobile() && <iframe title='pdf' src={lettersView} width='100%' height={551 - 129} />}
                </>
              )}
              {!pdf && (
                <Controller
                  name='body'
                  control={control}
                  rules={{ required: 'This field is required' }}
                  render={({ field }) => (
                    <div className='mt-4'>
                      <textarea
                        {...field}
                        className={cn('h-75 w-full resize-none border-none', 'focus:outline-none')}
                        placeholder='Write your text here...'
                        readOnly={props.data?.outgoing}
                        required
                      />
                    </div>
                  )}
                />
              )}
              <div
                className={cn(
                  'mt-5 flex items-center justify-between',
                  props.data?.attachments?.length && 'max-md:flex-col-reverse',
                  props.data && !props.data?.outgoing && 'max-md:flex-col-reverse',
                  !props.data?.outgoing && 'flex items-end',
                )}
              >
                {!props.data && (
                  <Controller
                    name='files'
                    control={control}
                    render={({ field: { onChange, value } }) => <UploadFile className='w-1/2' value={value} onChange={onChange} />}
                  />
                )}
                {props.data?.attachments?.length && <Attachments files={props.data.attachments} onDownload={downloadAttachmentHandler} />}
                {props.data && !props.data?.outgoing ? (
                  <Button className={cn('w-39.75', 'max-md:w-full')} onClick={replyHandler}>
                    Reply
                  </Button>
                ) : !props.data?.outgoing ? (
                  <Button className='w-39.75' type='submit' isLoading={formState.isSubmitting}>
                    Send
                  </Button>
                ) : (
                  <div />
                )}
                {props.data && (
                  <div className={cn('flex', 'max-md:w-full max-md:flex-col max-md:gap-0')}>
                    <div className={cn('flex', 'max-md:mb-3 max-md:items-center')}>
                      {pdf && (
                        <>
                          <div
                            className={cn(
                              'mx-3 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-tl-lg rounded-bl-lg',
                              'hover:underline',
                              'max-md:relative max-md:mx-0 max-md:h-9 max-md:w-full max-md:bg-[#fafafa]',
                            )}
                            onClick={() => saveAs(pdf, 'lettersPdf')}
                          >
                            <DownloadIcon className='max-md:hidden' />
                            <span className={cn('text-[9px]', 'max-md:hidden')}>Download</span>
                            <DownloadMobileIcon className={cn('hidden', 'max-md:inherit')} />
                          </div>
                          <div className={cn('z-100 hidden h-6 w-px flex-col items-center bg-[#4c4c4c20]', 'max-md:flex')} />
                          <div
                            className={cn(
                              'mx-3 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-tl-lg rounded-bl-lg',
                              'hover:underline',
                              'max-md:relative max-md:mx-0 max-md:h-9 max-md:w-full max-md:bg-[#fafafa]',
                            )}
                            onClick={() => PrintUtils.printPDF(pdf!)}
                          >
                            <PrintIcon className='max-md:hidden' />
                            <span className={cn('text-[9px]', 'max-md:hidden')}>Print</span>
                            <PrintMobileIcon className={cn('hidden', 'max-md:inherit')} />
                          </div>
                          <div className={cn('z-100 hidden h-6 w-px flex-col items-center bg-[#4c4c4c20]', 'max-md:flex')} />
                        </>
                      )}
                      <div
                        className={cn(
                          'mx-3 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-tl-lg rounded-bl-lg',
                          'hover:underline',
                          'max-md:relative max-md:mx-0 max-md:h-9 max-md:w-full max-md:bg-[#fafafa]',
                        )}
                        onClick={() => archiveLetterHandler(props.data!.id)}
                      >
                        <DownloadIcon className='max-md:hidden' />
                        <ArchiveMobileIcon className={cn('hidden', 'max-md:inherit')} />
                        <span className={cn('text-[9px]', 'max-md:hidden')}>Archive</span>
                      </div>
                      <div className={cn('z-100 hidden h-6 w-px flex-col items-center bg-[#4c4c4c20]', 'max-md:flex')} />
                      <div
                        className={cn(
                          'rounded-lb-lg mx-3 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-tr-lg',
                          'hover:underline',
                          'max-md:relative max-md:mx-0 max-md:h-9 max-md:w-full max-md:bg-[#fafafa]',
                        )}
                        onClick={deleteHandler}
                      >
                        <TrashMobileIcon className={cn('hidden', 'max-md:inherit')} />
                        <DeleteIcon className='max-md:hidden' />
                        <span className={cn('text-[9px]', 'max-md:hidden')}>Delete</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* <SentSuccessfullyModal
        isOpen={showSentSuccessfullyModal}
        onClose={() => {
          setShowSentSuccessfullyModal(false);
          closeHandler();
          props.onClose?.();
        }}
      /> */}
    </>
  );
};
